/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { RemoteError } from "abweb/dto/exception/RemoteError";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorContext } from "abweb/utils/error/ErrorContext";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";

/**
 * Types of extraInformation that can be set onto an error.
 * An enum to prevent collisions in names.
 * Values have leading underscores.
 */
export let ErrorExtra = /*#__PURE__*/function (ErrorExtra) {
  ErrorExtra["rpcServiceErrorUrl"] = "__rpcServiceErrorUrl";
  ErrorExtra["rpcServiceErrorCallStack"] = "__rpcServiceErrorCallStack";
  ErrorExtra["promiseUtilsForEachIndex"] = "__promiseUtilsForEachIndex";
  return ErrorExtra;
}({});

/**
 * Helper methods for rendering errors in TypeScript.
 */
export class ErrorUtils {
  /**
   * Add extra information onto an error
   */
  static setErrorExtra(err, errorExtra, value) {
    if (!!err && typeof err === "object") {
      err[errorExtra] = value;
    }
  }

  /**
   * Retrieve extra information from an error
   */
  static getErrorExtra(err, errorExtra) {
    return !!err && typeof err === "object" && err[errorExtra];
  }

  /**
   * Log an error thrown during service initialization.
   *
   * Note: it will be immediately logged, and then once the UI has been initialized enough it will be rendered.
   */
  static logServiceInitializationError(error) {
    LogAndRenderErrorPipeline.pipeline.handleErrorContext(ErrorContext.createFromError(error));
  }

  /**
   * @deprecated just throw a ClientError directly
   * Error display is now routed through a global error dispatching/resolving mechanism.
   * Just throw an error.
   * If you want to customize the error dialog title or context, just throw a ClientError directly.
   */
  static displayError(err, titleDescription, errorContext) {
    if (err != null && ClassUtils.isInstanceOf(err, RemoteError)) {
      throw err;
    }
    if (err != null && ClassUtils.isInstanceOf(err, Error) && (titleDescription || errorContext)) {
      throw new ClientError(err, titleDescription, errorContext);
    }
    if (ClassUtils.isInstanceOf(err, Error)) {
      throw err;
    } else {
      throw new ClientError(err, titleDescription, errorContext);
    }
  }
  static getErrorMsgFromObj(err) {
    if (err == null) {
      return "";
    }
    if (ClassUtils.isInstanceOf(err, Error)) {
      return err.message;
    }
    const errMsg = err.toString();
    // This is what you get when the class does not override the default toString implementation
    if (errMsg.indexOf("[object ") !== 0) {
      return errMsg;
    }
    // Last chance, try to use json stringify to get more meaningful data
    try {
      return JSON.stringify(err); // will fail if recursive
    } catch (jsonErrIgnored) {
      // Unlikely
      return "Unable to get meaningful error info from untyped error instance";
    }
  }
  static getExceptionClassNameFromRemoteStackTrace(remoteStackTrace, fullyQualified = false) {
    const remoteStackTraceLines = remoteStackTrace.split(/\s+/);
    let fullyQualifiedExceptionClassName = remoteStackTraceLines[0].split(":")[0];
    return fullyQualified ? fullyQualifiedExceptionClassName : fullyQualifiedExceptionClassName.split(".").reverse()[0];
  }
  static throw(errorMessage) {
    throw new Error(errorMessage);
  }
}

/**
 * An exception that wants customization to its error dialog.
 * Throwing a ClientError is the basic path for rendering an error on-screen to a user.
 * This tries to consume the complexity of different error types and turning them
 * into something reasonable for a non-technical human to process.
 * It can only be called after the i18n service has been initialized!
 */
export class ClientError extends Error {
  /**
   * A client error, seasoned to taste.
   * @param underlyingError Whatever object is received on failure. Ideally some Error/Exception type, but doesn't have to be.
   * @param titleDescription: A description of the high-level user operation going on. For example "Error While
   * Launching Profiler" would likely be good, but "Problem with XXfizzbuzz" is too technical. Might not be
   * shown based on the kind of error provided.
   * @param errorContext: A short message serving as an introduction to the problem (the context in which
   * the error occurred).
   * For example "An error occurred while launching the profiler. Please contact Ab Initio Support." is good,
   * but "Failure in XXfizzbuzz. Not enough buzz for fizz." isn't, and would be better delegated to the
   * application logs. Might not be shown based on the kind of error provided.
   */
  constructor(underlyingError, titleDescription, errorContext) {
    super();
    this.underlyingError = underlyingError;
    this.titleDescription = titleDescription;
    this.errorContext = errorContext;
  }
}
//# sourceMappingURL=ErrorUtils.js.map
